<template>
    <div class="user-view">
        <heading :title="title" tag="user-view-title">
            <div class="heading__buttons">
                <b-button v-if="canEdit" class="mr-2"
                          variant="primary"
                          :to="`/user/edit/${userId}`">
                    Edit User
                    <b-icon-pencil-square/>
                </b-button>
                <b-button v-if="canEdit" class="mr-2"
                          variant="primary"
                          :to="`/user/password/${userId}`">
                    Reset Password
                    <b-icon-arrow-clockwise/>
                </b-button>
                <b-button v-if="canMask" class="mr-2"
                          variant="primary"
                          @click="maskUser()">
                    Mask as User
                    <b-icon-emoji-sunglasses/>
                </b-button>
                <template v-if="userInfo.disabled">
                    <b-button v-if="canReactivate"
                              variant="warning"
                              @click="reactivateUser()">
                        Reactivate User
                        <b-icon-plus-square/>
                    </b-button>
                </template>
                <template v-else>
                    <b-button v-if="canDelete || canDeactivate"
                              variant="danger"
                              @click="canDelete ? deleteUser() : deactivateUser()">
                        Delete User
                        <b-icon-dash-square/>
                    </b-button>
                </template>
            </div>
        </heading>
        <b-row>
            <b-col class="col-md-6 offset-md-3 pt-lg-5">
                <b-card-body>
                    <view-table
                        :model="userFields"
                        :data="userInfo"
                        :fields="displayFields"/>
                </b-card-body>
            </b-col>
        </b-row>
        <!-- company users -->
        <b-row v-if="companyUsers" class="mt-4">
            <b-col>
                <h3>Associated Companies</h3>
            </b-col>
            <b-col class="text-right">
                <auth-template model="companyuser" action="create">
                    <b-button class="mb-2"
                              variant="primary"
                              :to="`/user/view/${userId}/company/create`">
                        Associate Company
                        <b-icon-plus-square/>
                    </b-button>
                </auth-template>
            </b-col>
        </b-row>
        <data-table ref="userCompanyTable"
                    :get-items="getCompanyUsers"
                    :fields="companyDisplayFields">
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'name'">
                    <router-link :to="`/company/view/${slotProps.row.item.company_id}`">
                        {{ slotProps.row.item[slotProps.row.field.key] }}
                    </router-link>
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_company_admin'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'approvalStatus'">
                    <template v-if="slotProps.row.item.approvalStatus === 'APPROVED'">
                        <timestamp :date="slotProps.row.item.approved"/>
                    </template>
                    <template v-else-if="slotProps.row.item.approvalStatus === 'NEEDS_APPROVAL'">
                        <b-button v-if="slotProps.row.item.canApprove"
                                  @click="approveCompanyUser(slotProps.row.item)" size="sm" variant="primary"
                                  class="mr-2">
                            Click to approve
                        </b-button>
                        <b-button v-if="slotProps.row.item.canApprove"
                                  @click="denyCompanyUser(slotProps.row.item)" size="sm" variant="danger"
                                  class="mr-2">
                            Deny Approval
                        </b-button>
                        <template v-else>
                            Needs Approval by Company Admin
                        </template>
                    </template>
                    <template v-else-if="slotProps.row.item.approvalStatus === 'APPROVAL_NA'">
                        Is Admin
                    </template>
                </div>
                <div v-else-if="slotProps.row.field.key === 'actions'" class="d-flex">
                    <b-button @click="editCompanyUser(slotProps.row.item)" size="sm" variant="secondary" class="mr-2">
                        <b-icon-pencil-square/>
                    </b-button>
                    <b-button @click="deleteCompanyUser(slotProps.row.item)" size="sm" variant="danger"
                              class="btn-icon">
                        <b-icon-trash/>
                    </b-button>
                </div>
            </template>
        </data-table>
        <!-- agency users -->
        <b-row v-if="agencyUsers" class="mt-4">
            <b-col>
                <h3>Associated Agencies</h3>
            </b-col>
            <b-col class="text-right">
                <auth-template model="agencyuser" action="create">
                    <b-button class="mb-2"
                              variant="primary"
                              :to="`/user/view/${userId}/agency/create`">
                        Associate Agency
                        <b-icon-plus-square/>
                    </b-button>
                </auth-template>
            </b-col>
        </b-row>
        <data-table ref="userAgencyTable"
                    :get-items="getAgencyUsers"
                    :fields="agencyDisplayFields">
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key === 'name'">
                    <router-link :to="`/agency/view/${slotProps.row.item.agency_id}`">
                        {{ slotProps.row.item[slotProps.row.field.key] }}
                    </router-link>
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_program_admin'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_owners_representative'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'is_project_manager'">
                    {{ slotProps.row.item[slotProps.row.field.key] ? '&#10004;' : null }}
                </div>
                <div v-else-if="slotProps.row.field.key === 'actions'">
                    <b-button @click="editAgencyUser(slotProps.row.item)" size="sm" variant="primary" class="mr-2">
                        <b-icon-pencil-square/>
                    </b-button>
                    <b-button @click="deleteAgencyUser(slotProps.row.item)" size="sm" variant="danger">
                        <b-icon-trash/>
                    </b-button>
                </div>
            </template>
        </data-table>
    </div>
</template>

<script>
import Vue from 'vue'
import UserFields from '@/models/User'
import Authorization from '@/providers/Authorization'

export default Vue.extend({
    name: 'UserView',
    props: {
        companyId: Number,
        userId: {
            type: Number,
            required: true
        },
    },
    data () {
        return {
            returnLabel: '',
            userInfo: {},
            agencyUsers: [],
            companyUsers: [],
            userFields: UserFields,
            canEdit: false,
            canMask: false,
            canDelete: false,
            canDeactivate: false,
            canReactivate: false,
        }
    },
    computed: {
        title () {
            const append = this.userInfo.disabled ? ' [DISABLED]' : ''
            return this.userInfo.full_name + append
        },
        displayFields () {
            return [
                'first_name',
                'last_name',
                'email',
                'phone_office',
                'phone_mobile',
                'is_superadmin',
                'is_admin',
                'is_auditor',
                'disabled',
            ]
        },
        agencyDisplayFields () {
            let agencyDisplayFields = [
                'name',
                'is_program_admin',
                'is_owners_representative',
                'is_project_manager',
                'company_name',
                'department',
            ]
            if (this.canEdit) {
                agencyDisplayFields.push('actions')
            }
            return agencyDisplayFields
        },
        companyDisplayFields () {
            let companyDisplayFields = [
                'name',
                'is_company_admin',
                'approvalStatus'
            ]
            if (this.canEdit) {
                companyDisplayFields.push('actions')
            }
            return companyDisplayFields
        },
    },
    methods: {
        getAgencyUsers () {
            return Promise.resolve(this.agencyUsers)
        },
        async editAgencyUser (agencyUser) {
            const can = await Authorization.can({ action: 'edit', model: 'agency', id: agencyUser.agency_id })
            if (can) {
                this.$router.push(`/user/view/${this.userId}/agency/${agencyUser.id}/edit`)
            } else {
                this.$toasted.info('Unable to edit this agency association')
            }
        },
        async deleteAgencyUser (agencyUser) {
            const can = Authorization.can({ action: 'edit', model: 'agency', arg: agencyUser.agency_id })
            if (can) {
                if (await this.$bvModal.msgBoxConfirm('Would you like to disassociate this user?')) {
                    try {
                        await this.$api.deleteItem(this, 'agency-user', agencyUser)
                        this.$toasted.info('Agency disassociated')
                        this.refresh()
                    } catch (e) {
                        this.$toasted.error(`Could not disassociate agency`)
                    }
                }
            } else {
                this.$toasted.error(`You are not authorized to edit this agency`)
            }
        },
        getCompanyUsers () {
            return Promise.resolve(this.companyUsers)
        },
        editCompanyUser (companyUser) {
            this.$router.push(`/user/view/${this.userId}/company/${companyUser.id}/edit`)
        },
        async deleteCompanyUser (companyUser) {
            if (await this.$bvModal.msgBoxConfirm('Would you like to disassociate this user?')) {
                this.$api.deleteItem(this, 'company-user', companyUser).then((response) => {
                    this.$toasted.info('Company disassociated')
                    this.refresh()
                }, (error) => {
                    this.$toasted.error('Could not disassociate from company -- there must be at least 1 admin user per company')
                })
            }
        },
        approveCompanyUser (companyUser) {
            this.$api.approveCompanyUser(this, companyUser.id).then((response) => {
                this.refresh()
            })
        },
        denyCompanyUser (companyUser) {
            this.$api.denyCompanyUser(this, companyUser.id).then((response) => {
                this.$router.push('/')
            })
        },
        maskUser () {
            this.$user.maskAsUser(this.userId)
        },
        async deleteUser () {
            const ok = await this.$bvModal.msgBoxConfirm('Would you like to delete this user? This action cannot be undone.')
            if (ok) {
                try {
                    await this.$api.deleteItem(this, 'user', this.userInfo)
                    this.$router.push('/user/list')
                    this.$toasted.info('User deleted')
                } catch (e) {
                    this.$toasted.error(`Could not delete user`)
                }
            }
        },
        async deactivateUser () {
            const ok = await this.$bvModal.msgBoxConfirm('Would you like to delete this user? This action cannot be undone.')
            if (ok) {
                try {
                    const userInfo = { ...this.userInfo, disabled: true }
                    await this.$api.updateItem(this, 'user', userInfo)
                    this.$router.push('/user/list')
                    this.$toasted.info('User deleted')
                } catch (e) {
                    this.$toasted.error(`Could not delete user`)
                }
            }
        },
        async reactivateUser () {
            const ok = await this.$bvModal.msgBoxConfirm('Would you like to reactivate this deleted user?')
            if (ok) {
                try {
                    const userInfo = { ...this.userInfo, disabled: false }
                    await this.$api.updateItem(this, 'user', userInfo)
                    this.$router.push('/user/list')
                    this.$toasted.info('User reactivated')
                } catch (e) {
                    this.$toasted.error(`Could not reactivate user`)
                }
            }
        },
        async refresh () {
            let data = await this.$api.getItem(this, 'user', this.userId)
            //  add agency name to agencyUsers list
            if (data.agencyUsers && data.agencies) {
                data.agencyUsers.forEach((agencyUser) => {
                    let agency_id = agencyUser.agency_id
                    data.agencies.forEach((agency) => {
                        if (agency.id === agency_id) {
                            agencyUser.name = agency.name
                        }
                    })
                })
            }
            //  add company name to companyUsers list
            if (data.companyUsers && data.companies) {
                data.companyUsers.forEach((companyUser) => {
                    let company_id = companyUser.company_id
                    data.companies.forEach((company) => {
                        if (company.id === company_id) {
                            companyUser.name = company.name
                        }
                    })
                })
            }
            this.userInfo = data
            this.companyUsers = data.companyUsers
            this.agencyUsers = data.agencyUsers
            this.$refs.userAgencyTable.refresh()
            this.$refs.userCompanyTable.refresh()
        }
    },
    async created () {
        await this.refresh()
        const notSelf = (this.userId !== this.$user.userId())
        Authorization.can({ action: 'edit', model: 'user', arg: this.userId })
            .then((can) => this.canEdit = can)
        Authorization.can({ action: 'mask', model: 'user', arg: this.userId })
            .then((can) => this.canMask = can && notSelf)
        Authorization.can({ action: 'delete', model: 'user', arg: this.userId })
            .then((can) => this.canDelete = can && notSelf)
        Authorization.can({ action: 'deactivate', model: 'user', arg: this.userId })
            .then((can) => this.canDeactivate = can && notSelf)
        Authorization.can({ action: 'reactivate', model: 'user', arg: this.userId })
            .then((can) => this.canReactivate = can && notSelf)
    },
    watch: {
        companyUsers (newVal, oldVal) {
            newVal.forEach((companyUser) => {
                Authorization.can({ action: 'approve', model: 'companyuser', arg: companyUser.id }).then((can) => {
                    companyUser.canApprove = can
                    this.$refs.userCompanyTable.refresh()
                })
            })
        }
    }

})
</script>

<style lang="scss">
    .user__action-buttons {
        @media (max-width: 575px) {
            .btn {
                width: 100%;
                margin: 6px 0;
            }
        }
        @media (min-width: 576px) and (max-width: 980px) {
            justify-content: space-between;
            .btn {
                width: 48%;
                margin: 6px 0;
            }
        }
        @media (min-width: 981px) {
            .btn {
                margin-right: 12px;
            }
        }
    }

</style>